/**
 * Utility functions for formatting data
 */
import i18n from "../i18n";

/**
 * Converts file size from bytes to MB format
 * @param sizeInBytes - Size in bytes as string
 * @returns Formatted size string in MB or localized "Unknown size" if invalid
 */
export const formatSizeInMB = (sizeInBytes: string | undefined): string => {
  if (!sizeInBytes) return i18n.t("common.unknownSize");

  const bytes = parseInt(sizeInBytes, 10);
  if (isNaN(bytes)) return i18n.t("common.unknownSize");

  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
};

/**
 * Formats file size in a human-readable format (KB, MB, GB)
 * @param sizeInBytes - Size in bytes as string or number
 * @returns Formatted size string
 */
export const formatFileSize = (
  sizeInBytes: string | number | undefined
): string => {
  if (!sizeInBytes) return i18n.t("common.unknownSize");

  const bytes =
    typeof sizeInBytes === "string" ? parseInt(sizeInBytes, 10) : sizeInBytes;
  if (isNaN(bytes)) return i18n.t("common.unknownSize");

  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const pad = (value: number): string => String(value).padStart(2, "0");

/**
 * Formats a date as DD-MM-YYYY (the app-wide date format).
 * @param date - Date string or Date object
 * @returns Formatted date string, e.g. "21-07-2026"
 */
export const formatDate = (date: string | Date | undefined): string => {
  if (!date) return i18n.t("common.unknownDate");

  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return i18n.t("common.invalidDate");

    return `${pad(dateObj.getDate())}-${pad(dateObj.getMonth() + 1)}-${dateObj.getFullYear()}`;
  } catch {
    return i18n.t("common.invalidDate");
  }
};

/**
 * Formats a date with time as DD-MM-YYYY HH:mm (optionally HH:mm:ss).
 * @param date - Date string or Date object
 * @param withSeconds - Include seconds in the time portion
 * @returns Formatted date-time string, e.g. "21-07-2026 14:32"
 */
export const formatDateTime = (
  date: string | Date | undefined,
  withSeconds = false
): string => {
  if (!date) return i18n.t("common.unknownDate");

  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return i18n.t("common.invalidDate");

    const timePart = withSeconds
      ? `${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}:${pad(dateObj.getSeconds())}`
      : `${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}`;

    return `${formatDate(dateObj)} ${timePart}`;
  } catch {
    return i18n.t("common.invalidDate");
  }
};
